import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useFetch, NeedTenant } from "../hooks/useData";
import { GlassCard, Loading, SectionTitle, Badge } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import client, { formatError } from "../lib/api";
import { Users, UserPlus, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["viewer", "connector_admin", "analyst", "admin"];

function Inner() {
  const { can, user: me } = useAuth();
  const { data, loading, reload } = useFetch("/users");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "viewer" });

  if (loading) return <Loading />;
  const users = data || [];

  const create = async () => {
    try { await client.post("/users", form); await reload(); setOpen(false); setForm({ name: "", email: "", password: "", role: "viewer" }); toast.success("User created"); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const changeRole = async (id, role) => { try { await client.patch(`/users/${id}`, { role }); await reload(); toast.success("Role updated"); } catch { toast.error("Failed"); } };
  const toggleStatus = async (id, status) => { try { await client.patch(`/users/${id}`, { status: status === "active" ? "suspended" : "active" }); await reload(); } catch { toast.error("Failed"); } };
  const del = async (id) => { try { await client.delete(`/users/${id}`); await reload(); toast.success("User removed"); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-5 pm-fade-up">
      <SectionTitle eyebrow="RBAC" title="Users & Roles" sub={`${users.length} members in this workspace`}
        right={can("admin") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-user-btn" className="rounded-xl pm-gradient text-white border-0 h-11"><UserPlus className="w-4 h-4 mr-2" />Invite user</Button></DialogTrigger>
            <DialogContent className="glass-strong">
              <DialogHeader><DialogTitle>Invite a new user</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" className="mt-1 bg-white/70 rounded-xl" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" className="mt-1 bg-white/70 rounded-xl" /></div>
                <div><Label>Temporary password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" className="mt-1 bg-white/70 rounded-xl" /></div>
                <div><Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="mt-1 bg-white/70 rounded-xl" data-testid="user-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
                </div>
                <Button onClick={create} data-testid="save-user" className="w-full rounded-xl pm-gradient text-white border-0">Create user</Button>
              </div>
            </DialogContent>
          </Dialog>
        )} />
      <GlassCard className="p-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400">
            <th className="p-3">User</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-white/50" data-testid={`user-row-${u.id}`}>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg pm-gradient-dark flex items-center justify-center text-white text-xs font-bold overflow-hidden">{u.avatar ? <img src={u.avatar} alt="" className="w-full h-full object-cover" /> : (u.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("")}</div>
                    <div><div className="font-semibold text-slate-800">{u.name}</div><div className="text-xs text-slate-400">{u.email}</div></div>
                  </div>
                </td>
                <td className="p-3">
                  {can("admin") && u.id !== me.id ? (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}><SelectTrigger className="w-40 h-9 bg-white/70 rounded-lg" data-testid={`role-select-${u.id}`}><Shield className="w-3.5 h-3.5 mr-1 text-cyan-600" /><SelectValue /></SelectTrigger><SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent></Select>
                  ) : <Badge value={u.role} />}
                </td>
                <td className="p-3"><button onClick={() => can("admin") && u.id !== me.id && toggleStatus(u.id, u.status)} data-testid={`status-${u.id}`}><Badge value={u.status || "active"} /></button></td>
                <td className="p-3 text-right">
                  {can("admin") && u.id !== me.id && <Button onClick={() => del(u.id)} data-testid={`del-user-${u.id}`} variant="ghost" size="sm" className="text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </GlassCard>
    </div>
  );
}

export default function UsersPage() { return <NeedTenant><Inner /></NeedTenant>; }
